import { HTMLMotionProps, motion } from "framer-motion";
import { I18n } from "@assets/strings";
import React from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useIsMobile } from "@utils/hooks";
import { desktop, mobile } from "@ui/styles/utils";
import { PrimaryBold } from "@ui/components/containers/primary";
import { AudioIcon, CameraIcon, FileIcon, ImageIcon, PlusIconRound } from "@ui/components/icons";
import { row, Row, stack } from "@ui/components/layout";
import { LoadingBar } from "@ui/components/loadingBar";

interface UploadAreaProps extends HTMLMotionProps<"div"> {
	dragging?: boolean;
	loading?: boolean;
	progress?: number;
}

export const UploadArea: React.FC<UploadAreaProps> = ({ dragging, loading, progress, ...htmlProps }) => {
	const isMobile = useIsMobile();

	return isMobile ? (
		<motion.div {...htmlProps}>
			<MainArea>
				{loading ? (
					<ClipLoader />
				) : (
					<>
						<PlusIconRound />
						<PrimaryBold>{I18n.t("asset_manager.upload.message_mobile")}</PrimaryBold>
					</>
				)}
			</MainArea>
		</motion.div>
	) : (
		<Container initial={{ scale: 1 }} animate={{ scale: dragging ? 1.1 : 1 }} {...htmlProps}>
			<MainArea>
				{loading ? (
					progress == undefined ? (
						<ClipLoader />
					) : (
						<LoadingBar progress={progress} />
					)
				) : (
					<>
						<PlusIconRound />
						<PrimaryBold>{I18n.t("asset_manager.upload.message")}</PrimaryBold>
					</>
				)}
			</MainArea>
			<Row gap={6} justify="space-between" align="stretch">
				<FileTypeWrapper>
					<ImageIcon />
					<div>{I18n.t("asset.image")}</div>
				</FileTypeWrapper>
				<FileTypeWrapper>
					<CameraIcon />
					<div>{I18n.t("asset.video")}</div>
				</FileTypeWrapper>
				<FileTypeWrapper>
					<FileIcon />
					<div>{I18n.t("asset.pdf")}</div>
				</FileTypeWrapper>
				<FileTypeWrapper>
					<AudioIcon />
					<div>{I18n.t("asset.audio")}</div>
				</FileTypeWrapper>
			</Row>
		</Container>
	);
};

const Container = styled(motion.div)`
	${stack(80, "flex-start", "stretch")}
	width: 447px;
	height: 352px;
	padding: 115px 100px 42px;
	border-radius: 8px;
	${({ theme }) => `
	background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${theme.colors.gray.replace(
		"#",
		"%23"
	)}' stroke-width='2' stroke-dasharray='12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
	`}
`;

const FileTypeWrapper = styled.div`
	${stack(8, "space-between", "center")};
	${({ theme }) => theme.textStyles.label};

	> svg {
		width: 18px;
		height: 18px;
		path {
			fill: ${({ theme }) => theme.colors.gray};
		}
	}
`;

const MainArea = styled.div`
	@media ${desktop} {
		${stack(18, "flex-start", "center")};
	}
	@media ${mobile} {
		${row(10, "flex-start", "center")};
		> div {
			color: ${({ theme }) => theme.colors.secondary};
		}
	}

	> svg {
		width: 40px;
		height: 40px;
		.main-path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}
`;
